import React from 'react';
import { Link } from 'react-router-dom';

import LegalPage, { Section, List, Callout, Table } from '../_gateway/LegalPage';
import {
  ADDRESS, COMPANY_NUMBER, CONTACT_EMAIL,
  COOKIE_HEADERS, NECESSARY_COOKIES, ANALYTICS_COOKIES, MARKETING_COOKIES,
} from '../_gateway/legal-content';
import { openCookieSettings } from '_components/atoms/cookie-consent';

const EmailLink = () => <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

const CookieSettingsLink = ({ children }) => (
  <button type="button" onClick={openCookieSettings} style={{ padding: 0, border: 0, background: 'none', color: '#1670c8', font: 'inherit', cursor: 'pointer' }}>
    {children}
  </button>
);

const subheadingStyle = { margin: '22px 0 4px', fontSize: 17, fontWeight: 700, color: '#1c2b4a' };

const WebsitePrivacy = () => (
  <LegalPage title="Website Privacy Policy" lastUpdated="7 October 2026">

    <Section id="about" number={1} title="About this policy">
      <p>
        This policy explains how PRM Assist Limited (&quot;PRM Assist&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) collects and uses
        personal data when you visit www.prmassist.com (the &quot;Website&quot;).
      </p>
      <p>
        The PRM Assist mobile app has its own Privacy Policy, available in the app and at{' '}
        <Link to="/privacy">www.prmassist.com/privacy</Link>.
      </p>
    </Section>

    <Section id="who" number={2} title="Who we are">
      <p>We are the data controller for personal data collected through the Website.</p>
      <List items={[
        <><strong>Address:</strong> {ADDRESS}</>,
        <><strong>Company number:</strong> {COMPANY_NUMBER}</>,
        <><strong>Contact:</strong> <EmailLink /></>,
      ]} />
    </Section>

    <Section id="data" number={3} title="Data we collect">
      <List items={[
        <><strong>Enquiry and demo request data:</strong> your name, email address, phone number (optional), job title, organisation, the areas you&apos;re interested in, and any message you send us.</>,
        <><strong>Usage data:</strong> your IP address, browser and device type, pages visited, time spent on pages, the site that referred you, and approximate location (country or city level).</>,
        <><strong>Cookie and pixel data:</strong> online identifiers set by our analytics and advertising partners. This is only collected with your consent (see section 8).</>,
      ]} />
      <Callout label="Important —">
        Please don&apos;t put health or disability details in our enquiry or demo request forms. If you need to request airport assistance, use the PRM Assist app,
        where that information is handled with your explicit consent.
      </Callout>
    </Section>

    <Section id="use" number={4} title="How we use your data and our legal basis">
      <Table
        caption="Purposes, data and legal basis"
        headers={['Purpose', 'Data', 'Legal basis (GDPR Art. 6)']}
        rows={[
          ['Replying to your enquiry or demo request', 'Enquiry and demo request data', 'Legitimate interest, or steps taken before entering a contract'],
          ['Running and securing the Website', 'Usage data', 'Legitimate interest'],
          ['Understanding how visitors use the Website', 'Analytics cookie data', 'Consent'],
          ['Measuring and personalising our advertising', 'Marketing pixel data', 'Consent'],
          ['Meeting our legal obligations', 'Any of the above', 'Legal obligation'],
        ]}
      />
      <p>We don&apos;t use your data for automated decision-making that has legal or similarly significant effects on you.</p>
    </Section>

    <Section id="sharing" number={5} title="Who we share data with">
      <List items={[
        <><strong>Hosting and website providers:</strong> Microsoft Azure</>,
        <><strong>Analytics:</strong> Google Ireland Limited (Google Analytics)</>,
        <><strong>Advertising platforms:</strong> Meta Platforms Ireland Ltd, LinkedIn Ireland Unlimited Company and Google</>,
        <><strong>Email and CRM providers:</strong> monday.com, used to manage your enquiries</>,
        <><strong>Professional advisers, public authorities, or buyers in a business transfer:</strong> only where this is necessary or required by law</>,
      ]} />
      <p>Each of our processors works under a Data Processing Agreement. We do not sell your personal data.</p>
    </Section>

    <Section id="transfers" number={6} title="International transfers">
      <p>Some of our providers may process data outside the EEA, including in the United States. Where they do, we rely on one of the following:</p>
      <List items={[
        'the EU–US Data Privacy Framework',
        'European Commission adequacy decisions',
        'Standard Contractual Clauses',
      ]} />
      <p>You can ask us for details by emailing <EmailLink />.</p>
    </Section>

    <Section id="retention" number={7} title="How long we keep data">
      <List items={[
        <><strong>Enquiries:</strong> 36 months after our last contact with you, unless they lead to a contract</>,
        <><strong>Analytics data:</strong> 14 months</>,
        <><strong>Cookies:</strong> see the cookie tables in section 8</>,
      ]} />
    </Section>

    <Section id="cookies" number={8} title="Cookies and similar technologies">
      <p>We use the following categories:</p>
      <List items={[
        <><strong>Strictly necessary:</strong> needed for the Website to work. These don&apos;t need your consent.</>,
        <><strong>Analytics (Google Analytics):</strong> help us understand how visitors use the Website.</>,
        <><strong>Marketing (e.g. Meta Pixel, LinkedIn Insight Tag):</strong> measure our ads and show relevant ads to you on other platforms.</>,
      ]} />
      <p>
        We only set analytics and marketing cookies if you agree to them through our cookie banner. You can change your choice at any time
        using the <CookieSettingsLink>Cookie settings</CookieSettingsLink> link in the footer. The full list of cookie names, providers and
        durations is below and in our <Link to="/cookies">Cookie Policy</Link>.
      </p>

      <h3 style={subheadingStyle}>Strictly necessary</h3>
      <Table caption="Strictly necessary cookies and storage" headers={COOKIE_HEADERS} rows={NECESSARY_COOKIES} />

      <h3 style={subheadingStyle}>Analytics (only with your consent)</h3>
      <Table caption="Analytics cookies and storage" headers={COOKIE_HEADERS} rows={ANALYTICS_COOKIES} />

      <h3 style={subheadingStyle}>Marketing (only with your consent)</h3>
      <Table caption="Marketing cookies" headers={COOKIE_HEADERS} rows={MARKETING_COOKIES} />
    </Section>

    <Section id="rights" number={9} title="Your rights">
      <p>You have the right to:</p>
      <List items={[
        'access your data',
        'have it corrected',
        'have it erased',
        'restrict how we process it',
        'receive it in a portable format',
        'object to processing based on legitimate interest or for direct marketing',
        'withdraw your consent at any time',
      ]} />
      <p>
        Email <EmailLink /> to use any of these rights. We&apos;ll reply within one month. You can also complain to the Data Protection
        Commission (<a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer">www.dataprotection.ie</a>) or to your
        local supervisory authority.
      </p>
    </Section>

    <Section id="children" number={10} title="Children">
      <p>
        The Website isn&apos;t aimed at children under 16, and we don&apos;t knowingly collect their data. If you think a child has sent us their
        data, please contact us and we will delete it.
      </p>
    </Section>

    <Section id="security" number={11} title="Security">
      <p>We use appropriate technical and organisational measures to protect your data. No online transmission is completely secure.</p>
    </Section>

    <Section id="links" number={12} title="Third-party links">
      <p>The Website links to app stores and other sites. Their own privacy policies apply to them.</p>
    </Section>

    <Section id="changes" number={13} title="Changes to this policy">
      <p>
        We&apos;ll post any updates here and change the &quot;Last updated&quot; date. If we make significant changes, we&apos;ll show a notice
        on the Website.
      </p>
    </Section>

    <Section id="contact" number={14} title="Contact">
      <p><EmailLink /><br />{ADDRESS}</p>
    </Section>

  </LegalPage>
);

export default WebsitePrivacy;
